import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Cookie, X } from 'lucide-react';

const CONSENT_KEY = 'vibesprint_consent';
const CONSENT_CHOICES_KEY = 'vibesprint_consent_choices';

const CookieConsentBanner = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [preferences, setPreferences] = useState(true);

  useEffect(() => {
    // Only show if no choice has been made yet
    try {
      const consent = localStorage.getItem(CONSENT_KEY);
      if (!consent) {
        // Small delay so it doesn't flash on initial paint
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch (err) {
      // localStorage might be blocked
    }
  }, []);

  const persist = (choice, choices) => {
    try {
      localStorage.setItem(CONSENT_KEY, choice);
      localStorage.setItem(CONSENT_CHOICES_KEY, JSON.stringify(choices));
    } catch (err) {
      /* ignore */
    }
  };

  const handleAcceptAll = () => {
    persist('accept_all', { necessary: true, analytics: true, preferences: true });
    setVisible(false);
  };

  const handleRejectOptional = () => {
    persist('reject_optional', { necessary: true, analytics: false, preferences: false });
    setVisible(false);
  };

  const handleSaveChoices = () => {
    persist('custom', { necessary: true, analytics, preferences });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[200] w-[calc(100%-2rem)] max-w-sm"
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-label="Cookie consent"
    >
      <div className="bg-card border rounded-xl shadow-lg p-3.5 backdrop-blur">
        <div className="flex items-start gap-2 mb-2">
          <div className="rounded-md bg-primary/10 p-1.5 flex-shrink-0">
            <Cookie className="h-3.5 w-3.5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm mb-0.5">We use cookies</h3>
            <p className="text-xs text-muted-foreground leading-snug">
              Essential to keep the app working. Optional ones help us improve.{' '}
              <button
                onClick={() => navigate('/cookies')}
                className="text-primary hover:underline font-medium"
                data-testid="cookie-banner-learn-more"
              >
                Learn more
              </button>
            </p>
          </div>
          <button
            type="button"
            onClick={handleRejectOptional}
            className="text-muted-foreground hover:text-foreground p-1 -m-1 rounded"
            aria-label="Reject optional cookies and close"
            data-testid="cookie-banner-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {customizing && (
          <div className="mt-4 mb-2 space-y-3 border-t pt-4" data-testid="cookie-banner-customize-panel">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Strictly necessary</p>
                <p className="text-xs text-muted-foreground">Required for the site to work</p>
              </div>
              <Switch checked disabled aria-label="Strictly necessary (always on)" />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Analytics</p>
                <p className="text-xs text-muted-foreground">Anonymous usage stats to improve VibeSprint</p>
              </div>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                data-testid="cookie-banner-toggle-analytics"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Preferences</p>
                <p className="text-xs text-muted-foreground">Remember UI choices (sort order, filters, theme)</p>
              </div>
              <Switch
                checked={preferences}
                onCheckedChange={setPreferences}
                data-testid="cookie-banner-toggle-preferences"
              />
            </div>
          </div>
        )}

        <div className="flex flex-col gap-1.5 mt-2">
          <div className="flex gap-1.5">
            {customizing ? (
              <Button
                size="sm"
                onClick={handleSaveChoices}
                className="btn-cta flex-1 h-7 text-xs px-2"
                data-testid="cookie-banner-save"
              >
                Save choices
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="btn-cta flex-1 h-7 text-xs px-2"
                data-testid="cookie-banner-accept-all"
              >
                Accept all
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleRejectOptional}
              className="flex-1 h-7 text-xs px-2"
              data-testid="cookie-banner-reject"
            >
              Reject
            </Button>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setCustomizing((s) => !s)}
            className="h-6 text-[11px] text-muted-foreground"
            data-testid="cookie-banner-customize"
          >
            {customizing ? 'Hide options' : 'Customize'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
